import React, { ReactNode } from 'react';

import * as S from './styles';

interface IProps {
  /** The image. Absent, blank or failing to load all fall back to the initial. */
  src?: string | null;
  /** What the thumbnail stands for. Its first character is the fallback. */
  name?: string | null;
  /**
   * A glyph to draw instead of an image or an initial — pass an inline `<svg>`.
   *
   * For a record that stands for an act rather than a thing, and so has no
   * picture and no meaningful first letter. A "Call the Waiter" order is the
   * case: there is no food in it, and an initial `C` would read as a menu item
   * called C.
   */
  icon?: ReactNode;
  /**
   * The tint. The design gives the three menu categories their own ground so a
   * column of thumbnails reads as a column of kinds; everything else is
   * neutral.
   */
  tone?: 'neutral' | 'food' | 'cold' | 'hot';
  className?: string;
}

/**
 * The design's `.thumb`: a --h-thumb rounded square with a hairline, holding
 * either an image or the first letter of what it stands for.
 *
 * The fallback is the point. Every image URL this panel renders goes through
 * imgproxy, and on the dev data every one of them 404s — the category icons,
 * the menu photos, the order thumbnails. A bare <img> against a 404 draws the
 * browser's broken-image glyph, which is the worst thing that can sit in a
 * table: it is ragged, it is a different size in every browser, and it reads
 * as an error in the panel rather than a gap in the data. An initial on a
 * quiet ground says the same thing — no picture — and keeps the row's rhythm.
 *
 * `key`-ing off `src` resets the failed flag when the row is recycled onto a
 * different record, which is what stops one broken image poisoning the cell
 * for every record that scrolls through it.
 */
const Thumb: React.FC<IProps> = ({ src, name, icon, tone = 'neutral', className }) => {
  const [failed, setFailed] = React.useState(false);

  React.useEffect(() => setFailed(false), [src]);

  const initial = (name || '').trim().charAt(0).toUpperCase();

  const content = src && !failed ? <S.Image src={src} alt="" onError={() => setFailed(true)} /> : icon || initial;

  return (
    <S.Thumb className={[tone, className].filter(Boolean).join(' ')} title={name || undefined}>
      {content}
    </S.Thumb>
  );
};

export default Thumb;
